import type { PurchaseOrder, ShippingDocumentsTabType } from "@shared/types";

export interface TabItem {
  id: ShippingDocumentsTabType;
  label: string;
  disabled: boolean;
}

export interface CarrierDetails {
  customerAccountNumber: string;
  isManifestEnabled?: boolean;
}

export interface BuildTabsParams {
  hasLabels: boolean;
  hasCancelledOrder: boolean;
  orderCount: number;
  shouldShowManifestTab: boolean;
  manifestPdfUrl?: string;
  initialTab: ShippingDocumentsTabType;
  t: (key: string) => string;
}

/**
 * Determines the effective initial tab given label availability and cancellation state.
 * Falls back to "packing-slip" whenever the requested tab cannot be shown.
 *
 * An explicit manifest request always wins: the manifest is a server-generated
 * document for the whole batch, so a cancelled order in the batch must not
 * redirect the preview to packing slips.
 */
export const getInitialTab = (
  hasCancelledOrder: boolean,
  hasLabels: boolean,
  requestedTab: ShippingDocumentsTabType
): ShippingDocumentsTabType => {
  if (requestedTab === "manifest") {
    return "manifest";
  }
  if (hasCancelledOrder) {
    return "packing-slip";
  }
  if (!hasLabels && (requestedTab === "shipping-label" || requestedTab === "combined")) {
    return "packing-slip";
  }
  return requestedTab;
};

/**
 * Returns true when the manifest tab should be shown.
 */
export const shouldShowManifest = (
  purchaseOrders: PurchaseOrder[] | undefined,
  manifestPdfUrl: string | undefined,
  carrierDetails: CarrierDetails[] | undefined
): boolean => {
  if (!purchaseOrders || purchaseOrders.length === 0) {
    return false;
  }
  if (!manifestPdfUrl) {
    return false;
  }
  for (const order of purchaseOrders) {
    const status = `${order.orderStatus}`;
    if (status === "LabelError" || status === "Error") {
      return false;
    }
  }
  const customerAccountSet = new Set<string>();
  for (const order of purchaseOrders) {
    if (order.customerAccountNumber) {
      customerAccountSet.add(order.customerAccountNumber);
    }
  }
  const customerAccountNumbers = Array.from(customerAccountSet);
  const isManifestEnabled = carrierDetails?.find((detail) =>
    customerAccountNumbers.some((accountNumber) => detail.customerAccountNumber.includes(accountNumber))
  )?.isManifestEnabled;
  return Boolean(isManifestEnabled);
};

/**
 * Builds the tabs array for the ShippingDocumentsDialog.
 */
export const buildTabs = ({
  hasLabels,
  hasCancelledOrder,
  orderCount,
  shouldShowManifestTab,
  manifestPdfUrl,
  initialTab,
  t,
}: BuildTabsParams): TabItem[] => {
  const isMultiOrder = orderCount > 1;

  if (manifestPdfUrl && initialTab === "manifest") {
    return [
      {
        id: "manifest",
        label: t("components.ShippingDocumentsDialog.manifest"),
        disabled: false,
      },
    ];
  }

  const baseTabs: TabItem[] = [
    {
      id: "packing-slip",
      label: isMultiOrder ? t("components.ShippingDocumentsDialog.packingSlips") : t("components.ShippingDocumentsDialog.packingSlip"),
      disabled: false,
    },
  ];

  if (hasLabels && !hasCancelledOrder) {
    baseTabs.unshift(
      {
        id: "combined",
        label: isMultiOrder
          ? t("components.ShippingDocumentsDialog.shippingLabelsAndPackingSlips")
          : t("components.ShippingDocumentsDialog.shippingLabelAndPackingSlip"),
        disabled: false,
      },
      {
        id: "shipping-label",
        label: isMultiOrder
          ? t("components.ShippingDocumentsDialog.shippingLabels")
          : t("components.ShippingDocumentsDialog.shippingLabel"),
        disabled: false,
      }
    );
  }

  if (shouldShowManifestTab) {
    baseTabs.push({
      id: "manifest",
      label: t("components.ShippingDocumentsDialog.manifest"),
      disabled: false,
    });
  }

  return baseTabs;
};

/**
 * Returns true when the group-by controls should be shown.
 */
export const shouldShowGroupBy = (isMulti: boolean, manifestPdfUrl?: string, initialTab?: ShippingDocumentsTabType): boolean => {
  return isMulti && !(manifestPdfUrl && initialTab === "manifest");
};
